import { uniqueId } from "lodash";
import { selectMarkets, selectNotificationCampaigns } from "../../../../../state/selectors";
import { getState } from "../../../../../state/store";

import { campaignCategories, deviceTypeOptions } from "../../../../../consts";
import { deviceModels } from "../../../consts/deviceModels";
import { audienceSetOptions } from "../consts/audienceSetOptions";
import { csvUtil, dateUtil } from "../../../../../utils";
import { appUserService } from "../../../../../services";

const formatFWUCampaignData = (fwuCampaignData) => {
  const { overviewData, audienceData, firmwareData } = fwuCampaignData;

  const isMarketSetOption = audienceData?.audienceSetOption === audienceSetOptions.MARKET;

  const startDate = overviewData.startDate;
  const startTime = overviewData.startTime;
  const startDateTime = dateUtil.combineDateTime(startDate, startTime);
  const markets = isMarketSetOption
    ? audienceData.selectedMarkets.filter((m) => m.id).map((m) => m.id)
    : [];
  const currentFwVersions = isMarketSetOption
    ? (audienceData?.versions ?? [])?.map((v) => v.id)
    : [];
  const newFwVersion = firmwareData.selectedVersions[0]?.version?.id;

  const dusns = !isMarketSetOption ? audienceData.dusns : [];

  const notificationCampaignName = overviewData.hasNotificationMessage
    ? overviewData.campaignName
    : "";

  return {
    campaign_name: overviewData.campaignName,
    model_type: audienceData.selectedDeviceModel.id,
    device_type: audienceData.selectedDeviceType.id.toUpperCase(),

    markets: markets,
    current_fw_versions: currentFwVersions,

    notification_campaign_name: notificationCampaignName,

    new_fw_version: newFwVersion,
    package_id: newFwVersion,
    start_ts: dateUtil.toDateStringNoTimezone(startDateTime),
    dusns: dusns
  };
};

const formatFwNotificationCampaignData = (fwuCampaignData) => {
  const { overviewData, audienceData } = fwuCampaignData;

  const hasNotificationCampaign = overviewData.hasNotificationMessage;
  const hasPushNotificationDate = overviewData.hasPushNotificationDate;
  const isMarketSetOption = audienceData?.audienceSetOption === audienceSetOptions.MARKET;

  if (!hasNotificationCampaign) {
    return null;
  }

  const notificationCamapignName = overviewData?.selectedNotificationName?.value;

  const startDate = hasPushNotificationDate
    ? overviewData.pushNotificationDate
    : overviewData.startDate;

  const startTime = hasPushNotificationDate
    ? overviewData.pushNotificationTime
    : overviewData.startTime;

  let startDateTime = dateUtil.combineDateTime(startDate, startTime);

  // Notifications should start half an hour after the campaign
  if (!hasPushNotificationDate) {
    startDateTime = dateUtil.addMinutes(startDateTime, 30);
  }

  const markets = isMarketSetOption
    ? audienceData.selectedMarkets.filter((m) => m.id).map((m) => m.id)
    : [];
  const currentFwVersions = isMarketSetOption
    ? (audienceData?.versions ?? [])?.map((v) => v.id)
    : [];

  const dusns = !isMarketSetOption ? audienceData.dusns : [];

  const result = {
    campaignName: overviewData.campaignName,
    notificationType: campaignCategories.firmwareUpdate,
    messageName: notificationCamapignName,
    details: {
      deviceTypes: [audienceData.selectedDeviceModel.id],
      firmwareVersions: currentFwVersions
    },
    startTs: dateUtil.toDateStringNoTimezone(startDateTime),
    markets,
    dusns
  };

  if (!result.details.firmwareVersions.length) {
    delete result.details.firmwareVersions;
  }

  if (!result.markets.length) {
    delete result.markets;
  }

  if (!result.dusns.length) {
    delete result.dusns;
  }

  return result;
};

const parseFWUCampaignData = (fwuCampaignData) => {
  const notificationCampaigns = selectNotificationCampaigns()(getState());

  const deviceModelOptions = Object.keys(deviceModels)?.map((device) => {
    return { id: device, value: device };
  });

  const userMarkets = selectMarkets()(getState());
  const marketOptions = (fwuCampaignData.markets ?? [])
    ?.map((m) => {
      return {
        id: m,
        value: m,
        isAvailable: appUserService.isAdmin() || userMarkets?.some((market) => market.market === m)
      };
    })
    .sort((first, second) => {
      return first.isAvailable === second.isAvailable ? 0 : second.isAvailable ? -1 : 1; // disabled comes first
    });

  const startDate = dateUtil.getUTCDate(new Date(fwuCampaignData?.startDate));

  const notificationCampaign = notificationCampaigns?.find(
    (campaign) => campaign.campaignName === fwuCampaignData.notification_campaign_name
  );

  const isDifferentDateForPushNotificationSelected = notificationCampaign?.startTs
    ? dateUtil.getDateDifferenceInMilliseconds(
        notificationCampaign.startTs,
        fwuCampaignData?.startDate
      ) > 1800000
    : false; // 30 minutes

  const notificationStartDate = isDifferentDateForPushNotificationSelected
    ? dateUtil.getUTCDate(new Date(notificationCampaign.startTs))
    : null;

  const overviewData = {
    campaignName: fwuCampaignData?.campaignName,
    startDate: startDate,
    startTime: startDate,
    hasNotificationMessage: !!notificationCampaign,
    hasPushNotificationDate: isDifferentDateForPushNotificationSelected,
    pushNotificationDate: notificationStartDate,
    pushNotificationTime: notificationStartDate,
    selectedNotificationName: notificationCampaign
      ? {
          id: notificationCampaign?.messageName,
          value: notificationCampaign?.messageName
        }
      : null
  };

  const markets = fwuCampaignData?.markets ?? [];
  const isMarketSetOption = !!markets?.length;
  const dusns = fwuCampaignData?.dusns ?? [];

  const selectedDeviceModel = deviceModelOptions.find(
    (opt) => opt.id === fwuCampaignData?.deviceModel
  );
  const selectedDeviceType = deviceTypeOptions.filter((opt) => opt.id === "CHARGER")[0];
  const selectedMarkets = marketOptions.filter((opt) => markets.includes(opt.id));

  const versions = fwuCampaignData?.currentFirmwares.map((version) => {
    return { id: version, value: version };
  });

  const audienceData = {
    audienceSetOption: isMarketSetOption
      ? audienceSetOptions.MARKET
      : audienceSetOptions.DEVICE_LIST,
    selectedDeviceModel,
    selectedDeviceType,
    selectedMarkets,
    dusns,
    versions
  };

  if (!isMarketSetOption) {
    audienceData.listOfDevicesFile = csvUtil.createCSVFile(
      dusns.map((dusn) => {
        return { dusn };
      })
    );
  }

  const versionTo = fwuCampaignData?.versionTo;
  const selectedVersions = [{ id: uniqueId(), version: { id: versionTo, value: versionTo } }];

  const firmwareData = { selectedVersions };

  return { overviewData, audienceData, firmwareData };
};

export const formatUtils = {
  formatFWUCampaignData,
  formatFwNotificationCampaignData,
  parseFWUCampaignData
};
